import React from 'react';
import createStore from './store/createStore';
import {DataRouter, connectRouter, ConnectedRouter} from './router/DataRouter';
import AppProvider from './providers/AppProvider';
import createAppContainer from './containers/createAppContainer';
import createDataEngine from './dataEngine/createDataEngine';
import createHistory from './router/createHistory';
import ReducerStore from './reducers/ReducerStore';
import AppStateComponent from './components/AppStateComponent';
import initContainerData from './store/initContainerData';

import {actions, default as defaultReducers} from './reducers/defaultReducers';

export default function appFactory (App, opts = {}) {
    // fetch the data from our initContainerData singleton with reducers + datasources
    // populated from our app's data containers and add here for registration with store creation
    const { reducers: initReducers, datasources: initDatasources } = initContainerData.get();
    opts.reducers = { ...opts.reducers, ...initReducers };
    opts.datasources = { ...opts.datasources, ...initDatasources };

    const {
        reducers = {},
        DataEngine,
        renderState = false,
        useLoader = true,
        initState = {},
        routes = false,
        server = false,
        basename = '/',
        useMemoryHistory = false
    } = opts;
    const storeEnhancements = {};

    // Compose custom connectors first, they can potentially
    // rely on output from the dataEngine.
    const AppContainer = createAppContainer(opts, App);
    // This will need to be a more strict check at some point.
    // Need to iron out shared engine implementation details.
    const componentDataEngine = opts.dataEngine = DataEngine ? new DataEngine(opts) : createDataEngine(opts);
    const reducerStore = opts.reducerStore = new ReducerStore();

    reducerStore.add(defaultReducers);

    if (routes) {
        const history = storeEnhancements.history = createHistory({server, basename, useMemoryHistory});
        reducerStore.add({router: connectRouter(history)});
    }

    reducerStore.add(componentDataEngine.datasourceReducers);
    reducerStore.add(componentDataEngine.dataEngineReducers);
    reducerStore.add(reducers);

    const { store } = createStore(opts, storeEnhancements);
    const { dispatch } = store;

    // pre-bind for dispatch
    componentDataEngine.dynamicDispatch(dispatch);

    const handleComponentDataError = () => {
        store.dispatch(actions.componentRenderError);
    };

    let appBase = (
        <React.Fragment>
            <AppContainer componentDataEngine={componentDataEngine} handleComponentDataError={handleComponentDataError} useLoader={useLoader} />
            { renderState ? <AppStateComponent appState={store} key='app-state' /> : ''}
        </React.Fragment>
    );

    let dataRouter;

    if (routes) {
        dataRouter = new DataRouter(routes, componentDataEngine, store);
        appBase = (
            <ConnectedRouter history={storeEnhancements.history} noInitialPop>
                {appBase}
            </ConnectedRouter>
        );
    }

    const app = (
        <AppProvider store={store} key='bdata-app-provider'>
            {appBase}
        </AppProvider>
    );

    return {
        app,
        componentDataEngine,
        store,
        prefetch () {
            componentDataEngine.init(app, initState);
            if (routes && dataRouter) {
                return dataRouter.requestRouteData(opts.route);
            } else {
                const fetchData = App.fetchData(initState, dispatch);
                const { then, datasource, dataParams } = fetchData;
                if (typeof then === 'function') {
                    return fetchData;
                } else if (typeof fetchData === 'function') {
                    return Promise.resolve(fetchData());
                }
                return componentDataEngine.fetchAndStore(datasource, dataParams);
            }
        }
    };
};
